import { supabase } from "@/integrations/supabase/client";

// ============================================================================
// Document Preview Utilities
// ============================================================================
// Central helper functions for document preview across the application.
// Separates concerns: URL handling, file type detection, and download logic.
// ============================================================================

export type SupportedBucket = "employee-documents" | "applicant-documents" | "employee-profiles";

export interface DocumentInfo {
  id?: string;
  file_path: string;
  file_name: string;
  mime_type: string;
}

// Allowed buckets for security validation
const ALLOWED_BUCKETS: SupportedBucket[] = ["employee-documents", "applicant-documents", "employee-profiles"];

/**
 * Validates if a bucket name is allowed for document operations
 */
export function isValidBucket(bucket: string): bucket is SupportedBucket {
  return ALLOWED_BUCKETS.includes(bucket as SupportedBucket);
}

/**
 * Downloads a file from Supabase Storage and returns a blob URL.
 * Blob URLs are same-origin, avoiding CORS/CSP issues across all browsers.
 */
export async function downloadAndCreateBlobUrl(
  bucket: SupportedBucket,
  filePath: string,
  mimeType?: string
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .download(filePath);

  if (error) {
    console.error("Download error:", error);
    throw new Error("Datei konnte nicht geladen werden");
  }

  // Ensure the blob has the correct MIME type for proper rendering
  const finalMimeType = mimeType || data.type || "application/octet-stream";
  const typedBlob = new Blob([data], { type: finalMimeType });
  return URL.createObjectURL(typedBlob);
}

/**
 * Triggers a file download using a blob URL
 */
export async function downloadFile(
  bucket: SupportedBucket,
  filePath: string,
  fileName: string
): Promise<void> {
  const blobUrl = await downloadAndCreateBlobUrl(bucket, filePath);
  
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  
  URL.revokeObjectURL(blobUrl);
}

/**
 * Builds a URL for the standalone preview page route
 * Includes document ID and type for GDPR audit logging
 */
export function buildPreviewPageUrl(
  bucket: SupportedBucket,
  document: DocumentInfo,
  documentType: "employee" | "applicant" = "employee"
): string {
  const params = new URLSearchParams({
    bucket,
    path: document.file_path,
    name: document.file_name,
    type: document.mime_type || "",
  });
  
  // Include document ID for audit logging if available
  if (document.id) {
    params.set("docId", document.id);
    params.set("docType", documentType);
  }
  
  return `/preview?${params.toString()}`;
}

/**
 * Opens document in a new tab using the preview page route
 * Includes document metadata for GDPR audit logging
 */
export function openInNewTab(
  bucket: SupportedBucket,
  document: DocumentInfo,
  documentType: "employee" | "applicant" = "employee"
): void {
  const url = buildPreviewPageUrl(bucket, document, documentType);
  window.open(url, "_blank", "noopener,noreferrer");
}

// ============================================================================
// File Type Detection
// ============================================================================

/**
 * Checks if the file is a PDF based on MIME type or file extension
 */
export function isPdfFile(mimeType: string, fileName?: string): boolean {
  if (mimeType === "application/pdf") return true;
  if (fileName?.toLowerCase().endsWith(".pdf")) return true;
  return false;
}

/**
 * Checks if the file is an image based on MIME type or file extension
 */
export function isImageFile(mimeType: string, fileName?: string): boolean {
  if (mimeType?.startsWith("image/")) return true;
  if (fileName && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName)) return true;
  return false;
}

/**
 * Checks if the file is a Word document
 */
export function isWordFile(mimeType: string, fileName?: string): boolean {
  if (mimeType === "application/msword") return true;
  if (mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return true;
  if (fileName && /\.(doc|docx)$/i.test(fileName)) return true;
  return false;
}

/**
 * Checks if the file is an Excel spreadsheet
 */
export function isExcelFile(mimeType: string, fileName?: string): boolean {
  if (mimeType === "application/vnd.ms-excel") return true;
  if (mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") return true;
  if (fileName && /\.(xls|xlsx)$/i.test(fileName)) return true;
  return false;
}

/**
 * Checks if the file is an Office document (Word or Excel)
 */
export function isOfficeFile(mimeType: string, fileName?: string): boolean {
  return isWordFile(mimeType, fileName) || isExcelFile(mimeType, fileName);
}

/**
 * Checks if the file can be previewed in the browser
 */
export function canPreviewInBrowser(mimeType: string, fileName?: string): boolean {
  return isPdfFile(mimeType, fileName) || isImageFile(mimeType, fileName) || isOfficeFile(mimeType, fileName);
}

/**
 * Returns a human-readable label for the file type
 */
export function getFileTypeLabel(mimeType: string): string {
  if (mimeType?.startsWith("image/")) return "Bild";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType?.includes("word") || mimeType?.includes("document")) return "Word-Dokument";
  if (mimeType?.includes("excel") || mimeType?.includes("spreadsheet")) return "Excel-Tabelle";
  if (mimeType?.includes("powerpoint") || mimeType?.includes("presentation")) return "PowerPoint";
  return "Datei";
}
